import { useImagePickerCamera } from 'app/hooks/use-imagepicker-camera';
import { useGetInterlocutor } from 'app/network/chat';
import { MESSAGE_ID_LOADING, THUMBS_UP } from 'app/network/chat/message';
import { MessageKind } from 'app/types/chat';
import type { FileMessage } from 'app/types/chat';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import { showFileTooLargeToastIfNeeded } from 'app/lib/upload-errors';
import { isMobileNative } from 'app/utils/web-helpers';
import type { ImagePickerSuccessResult } from 'expo-image-picker';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import type { ChannelToolbarProps } from './channel-toolbar.types';
import RNHapticFeedback from 'react-native-haptic-feedback';

export function useChannelToolbarComposer({
  channel,
  listing,
  setMessageBeingSent,
  sentNoTextMessages,
  textContent,
  setTextContent,
  getMyselfInChannel,
  createMessage,
  createTextMessage,
}: ChannelToolbarProps) {
  const { t } = useTranslation();
  const getInterlocutor = useGetInterlocutor();

  const doVibrate = () => {
    if (!isMobileNative) return;
    try {
      RNHapticFeedback.trigger('impactLight', {
        enableVibrateFallback: true,
        ignoreAndroidSystemSettings: false,
      });
    } catch {
      // ignore haptic errors so they cannot abort the calling action
    }
  };

  const initializedRef = useRef(false);
  const isSendingTextRef = useRef(false);

  // Reset sending state once the text has been cleared (message was sent)
  useEffect(() => {
    if (textContent === '') {
      isSendingTextRef.current = false;
    }
  }, [textContent]);

  useEffect(() => {
    if (channel && listing && sentNoTextMessages && !initializedRef.current) {
      const name = getInterlocutor(channel)?.nickname;
      if (name) {
        const timeout = setTimeout(() => {
          initializedRef.current = true;
          setTextContent(
            t('toolbar.defaultValue', {
              ns: 'one-channel',
              name: isFeatureEnabled(FeatureFlag.lastName) ? name.split(' ')[0] : '',
            }).replace(/\s+/g, ' '),
          );
        }, 0);
        return () => clearTimeout(timeout);
      }
    }
  }, [channel, getInterlocutor, listing, sentNoTextMessages, t, setTextContent]);

  const onFileSent = (fileMessage: FileMessage) => {
    setTextContent('');
    setMessageBeingSent(fileMessage);
  };

  const createFileMessage = (result: ImagePickerSuccessResult) => {
    if (!channel) {
      throw new Error('Could not create file message because channel is missing');
    }

    createMessage.mutate(
      {
        channel_url: channel.channel_url,
        file: result.assets[0]!,
        type: MessageKind.File,
        isFirstMessage: !channel.last_message,
        listingUserId: getInterlocutor(channel)?.user_id,
      },
      {
        onError: (error) => {
          setMessageBeingSent(undefined);
          showFileTooLargeToastIfNeeded(error);
        },
      },
    );
    const myself = getMyselfInChannel(channel);
    if (!myself) return;
    onFileSent({
      file: { url: result.assets[0]!.uri },
      type: MessageKind.File,
      channel_url: channel.channel_url,
      created_at: Date.now(),
      user: myself,
      viewed: false,
      message_id: MESSAGE_ID_LOADING,
    });
  };

  const onLikePress = () => {
    doVibrate();
    createTextMessage(THUMBS_UP);
  };

  const onSendPress = () => {
    if (!channel || isSendingTextRef.current || textContent.trim().length === 0) return;

    isSendingTextRef.current = true;
    doVibrate();
    createTextMessage(textContent);
  };

  const onKeyPress = (e: { nativeEvent: { key: string; shiftKey?: boolean } }) => {
    if (isMobileNative) return;
    if (e.nativeEvent.key === 'Enter' && !e.nativeEvent.shiftKey) {
      (e as unknown as { preventDefault: () => void }).preventDefault();
      if (textContent.trim().length > 0) {
        onSendPress();
      }
    }
  };

  const { onUseImagePickerCamera, settingsPromptState } = useImagePickerCamera(createFileMessage, undefined, {
    allowsEditing: Platform.OS !== 'ios',
    aspect: undefined,
    quality: 0.5,
    allowsMultipleSelection: false,
  });

  const isActionsDisabled = createMessage.isPending || !channel;

  return {
    onUseImagePickerCamera,
    settingsPromptState,
    onLikePress,
    onSendPress,
    onKeyPress,
    isActionsDisabled,
  };
}
